import { createTheme } from '@mui/material/styles';

const customTheme = createTheme({
  palette: {
    primary: {
      main: '#2F80ED',
      contrastText: '#fff',
      dark: '#447EEB',
    },
    primaryLight: {
      main: 'rgba(68, 126, 235, 0.3)',
      contrastText: '#fff',
    },
    secondary: {
      main: '#9B51E0',
      contrastText: '#000',
    },
    warning: {
      main: '#f00f00',
      contrastText: '#e5484d',
      light: '#fffB00',
    },
    lorealPurple: {
      main: '#8043B9',
      contrastText: '#fff',
    },
    lorealBlue: {
      main: '#2D9CDB',
      contrastText: '#000',
    },
    success: {
      main: '#27AE60',
      contrastText: '#000',
      dark: '#388C2D',
    },
    error: {
      main: '#EB5757',
      contrastText: '#000',
    },
    lightGrey: {
      main: '#FAFAFA',
      contrastText: '#000',
    },

    border: {
      main: '#c7d9f9',
      contrastText: '#000',
    },
    borderDark: {
      main: '#7ba5f1',
      contrastText: '#000',
    },
    textLight: {
      main: '#525F81',
      contrastText: '#fff',
    },
    textDark: {
      main: '#3B455E',
      contrastText: '#fff',
    },
    totalBackground: {
      main: '#DDE9F9',
      contrastText: '#000',
    },
    mediaPlanTable: {
      background: '#F2F8FF',
      headerDivider: '#EDEDED',
      textColor: '#525F81',
    },
    tableBackground: {
      main: '#F2F8FF',
      contrastText: '#000',
    },
    boxShadow: {
      main: 'rgba(82,95,129,0.35)',
      contrastText: '#fff',
    },
    ctaButton: {
      background: '#1976d2',
      hoverBackground: '#1165b8',
    },
    oohTable: {
      background: {
        reach: '#E6EAF3',
        total: '#c7d8f9',
      },
    },
    grpTable: {
      background: {
        month: '#C8DEF5',
        week: '#A2C9EE',
        day: '#75ACE5',
        default: '#DFDFDF',
        totals: '#C4C4C4',
        nonEditable: '#EDEDED',
      },
      text: {
        main: '#525F81',
        light: '#7b7b7b',
      },
    },
    slider: {
      main: {
        track: '#447EEB',
        rail: '#DFDFDF',
        thumb: '#447EEB',
      },
      disabled: {
        track: '#989fb0',
        rail: '#757575',
        thumb: '#DFDFDF',
      },
      mediaPlan: {
        track: '#CCCCCC',
      },
    },
    gray: {
      main: '#BDBDBD',
      silver: '#676767',
      chrome: '#dddfe4',
      brightGray: '#EEEEEE',
    },
  },
  typography: (palette) => ({
    fontFamily: 'Lato, sans-serif',
    allVariants: {
      color: palette.textLight.main,
    },
    button: {
      textTransform: 'none',
    },
  }),
  components: {
    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme }) => ({
          height: '0.2rem',
          color: theme.palette.primary.main,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          letterSpacing: '0.04rem',
          opacity: 1,
          fontWeight: '700',
          '&.Mui-selected': {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        contained: ({ theme }) => ({
          fontWeight: 700,
          color: theme.palette.primary.contrastText,
          background: theme.palette.primary.main,
          boxShadow: `0px 4px 9px ${theme.palette.boxShadow.main}`,
          borderRadius: '30px',
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: '45px',
          minHeight: '45px',
          fontSize: '14px',
          color: theme.palette.textDark.main,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${theme.palette.primaryLight.main}`,
            cursor: 'not-allowed',
            pointerEvents: 'all !important',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: 5,
            borderColor: theme.palette.borderDark.main,
          },
          '.MuiInputBase-input': {
            padding: '12px 14px',
            height: '100%',
            boxSizing: 'border-box',
          },
          '::placeholder': {
            fontSize: '14px',
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: ({ theme }) => ({
          height: '45px',
          minHeight: '45px',
          borderColor: theme.palette.primary.main,
          color: theme.palette.textLight.main,
          backgroundColor: theme.palette.common.white,
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid ${theme.palette.border.main}`,
          borderRadius: '10px',
          boxShadow: 'none',
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.textLight.main,
        }),
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.tableBackground.main,
        }),
        content: {
          height: '7rem',
          padding: '0rem 1rem',
          justifyContent: 'space-between',
          '&.Mui-expanded': {
            margin: '12px 0',
            padding: '0rem 1rem',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: '45px',
          minHeight: '45px',
          fontWeight: 500,
          fontSize: '14px',
          backgroundColor: theme.palette.common.white,
          '& .MuiInputBase-root': {
            height: '100%',
          },
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: '14px',
          backgroundColor: theme.palette.common.white,
          top: '-4px',

          '&.Mui-focused': {
            top: '2px',
            left: '-3px',
            padding: '0 4px',
          },

          '&.MuiFormLabel-filled': {
            top: '2px',
            left: '-3px',
            padding: '0 4px',
          },

          '&.MuiInputLabel-shrink': {
            top: '2px',
            left: '-3px',
            padding: '0 4px',
          },
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '14px',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&.MuiInputLabel-root': {
            maxWidth: 'calc(100% - 3rem)',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '&.MuiFormHelperText-root': {
            maxWidth: 'calc(100% - 3rem)',
          },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          height: '8px',
          borderRadius: '40px',
          marginBottom: '7px',
          paddingTop: '6px',
        },
        track: ({ theme }) => ({
          backgroundColor: theme.palette.slider.main.track,
          '.Mui-disabled &': {
            backgroundColor: theme.palette.slider.disabled.track,
          },
        }),
        rail: ({ theme }) => ({
          backgroundColor: theme.palette.slider.main.rail,
          '&.Mui-disabled': {
            backgroundColor: theme.palette.slider.disabled.rail,
          },
        }),
        thumb: ({ theme }) => ({
          width: '20px',
          height: '20px',
          backgroundColor: theme.palette.common.white,
          border: `2px solid ${theme.palette.slider.main.thumb}`,
          boxShadow: 'none',
          '&.Mui-disabled': {
            border: `2px solid ${theme.palette.slider.disabled.thumb}`,
          },
        }),
        valueLabel: ({ theme }) => ({
          backgroundColor: theme.palette.slider.main.track,
          color: theme.palette.common.white,
          borderRadius: '4px',
          fontSize: '11px',
          padding: '2px 4px',
          top: '-7px',
        }),
        mark: {
          display: 'none',
        },
        markLabel: ({ theme }) => ({
          color: theme.palette.textLight.main,
          fontSize: '11px',
          top: '21px',
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        listbox: {
          '.MuiAutocomplete-option': {
            padding: 0,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.gray.main,
        }),
      },
    },
  },
});

declare module '@mui/material/styles' {
  interface Palette {
    lightGrey: Palette['primary'];
    border: Palette['primary'];
    textLight: Palette['primary'];
    textDark: Palette['primary'];
    tableBackground: Palette['primary'];
    lorealPurple: Palette['primary'];
    lorealBlue: Palette['primary'];
    table: Palette['primary'];
    totalBackground: Palette['primary'];
    boxShadow: Palette['primary'];
    borderDark: Palette['primary'];
    primaryLight: Palette['primary'];
    slider: {
      main: {
        track: string;
        rail: string;
        thumb: string;
      };
      mediaPlan: {
        track: string;
      };
      disabled: {
        track: string;
        rail: string;
        thumb: string;
      };
    };
    mediaPlanTable: {
      background: string;
      headerDivider: string;
      textColor: string;
    };
    ctaButton: {
      background: string;
      hoverBackground: string;
    };
    oohTable: {
      background: {
        reach: string;
        total: string;
      };
    };
    grpTable: {
      background: {
        month: string;
        week: string;
        day: string;
        default: string;
        totals: string;
        nonEditable: string;
      };
      text: {
        main: string;
        light: string;
      };
    };
    gray: {
      main: string;
      silver: string;
      chrome: string;
      brightGray: string;
    };
  }

  interface PaletteOptions {
    lightGrey: PaletteOptions['primary'];
    border: PaletteOptions['primary'];
    textLight: PaletteOptions['primary'];
    textDark: PaletteOptions['primary'];
    tableBackground: PaletteOptions['primary'];
    lorealPurple: PaletteOptions['primary'];
    lorealBlue: PaletteOptions['primary'];
    totalBackground: PaletteOptions['primary'];
    boxShadow: PaletteOptions['primary'];
    borderDark: PaletteOptions['primary'];
    primaryLight: PaletteOptions['primary'];
    slider: {
      main: {
        track: string;
        rail: string;
        thumb: string;
      };
      mediaPlan: {
        track: string;
      };
      disabled: {
        track: string;
        rail: string;
        thumb: string;
      };
    };
    mediaPlanTable: {
      background: string;
      headerDivider: string;
      textColor: string;
    };
    ctaButton: {
      background: string;
      hoverBackground: string;
    };
    oohTable: {
      background: {
        reach: string;
        total: string;
      };
    };
    grpTable: {
      background: {
        month: string;
        week: string;
        day: string;
        default: string;
        totals: string;
        nonEditable: string;
      };
      text: {
        main: string;
        light: string;
      };
    };
    gray: {
      main: string;
      silver: string;
      chrome: string;
      brightGray: string;
    };
  }
}

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsColorOverrides {
    textDark: true;
    textLight: true;
  }
}

export default customTheme;
